/**
 * Relações pessoa↔pessoa — o painel que fortalece o grafo com laços entre pessoas:
 * pai, mãe, filhos, irmãos, primos, cônjuge, amigos, sócios. A semântica: "a pessoa
 * escolhida é <tipo> desta". O backend cria a recíproca sozinho (registrar "B é pai
 * de A" faz A aparecer como filho/filha de B), então cada lado vê o laço do seu ponto
 * de vista. Ambas as pontas são pessoas cadastradas — é isso que vira aresta no grafo.
 */
import { Pencil, Trash2, Users2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboBuscaEntidade, type ConfigBusca, type ItemBusca } from '@/components/crud/ComboBuscaEntidade';
import { CamposVinculo, periodoLegivel } from '@/components/crud/CamposVinculo';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Chip } from '@/components/ui/misc';
import { api, type RelacaoPessoalCad } from '@/lib/api';
import { formatar } from '@/lib/mascaras';
import { RELACOES, rotuloRelacao } from '@/lib/relacoesPessoais';

const BUSCA_PESSOA: ConfigBusca = {
  endpoint: '/cadastro/pessoas',
  rotulo: (p) => String(p.nome ?? ''),
  sub: (p) => (p.cpf ? formatar(String(p.cpf), 'cpf') : null),
  placeholder: 'Buscar pessoa por nome ou CPF…',
  camposRapido: [
    { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true },
    { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
  ],
};

export function PainelRelacoes({ pessoaId }: { pessoaId: string }) {
  const base = `/cadastro/pessoas/${pessoaId}/relacoes`;
  const [relacoes, setRelacoes] = useState<RelacaoPessoalCad[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [alvo, setAlvo] = useState<ItemBusca | null>(null);
  const [tipo, setTipo] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState<string | null>(null); // `${relacionada_id}/${tipo}/${data_inicio}`

  async function carregar() {
    const r = await api.get<RelacaoPessoalCad[]>(base);
    setCarregando(false);
    if (r.ok) setRelacoes(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  function limparForm() {
    setAlvo(null);
    setTipo('');
    setInicio('');
    setFim('');
    setObservacoes('');
  }

  async function adicionar(): Promise<boolean> {
    if (!alvo || !tipo || salvando) return false;
    setSalvando(true);
    const r = await api.post(base, {
      relacionada_id: alvo.id,
      tipo,
      data_inicio: inicio || null,
      data_fim: fim || null,
      observacoes: observacoes.trim() || null,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    toast.success(`${String(alvo.nome)} vinculado como ${rotuloRelacao(tipo)}.`);
    limparForm();
    void carregar();
    return true;
  }

  async function remover(rel: RelacaoPessoalCad) {
    const r = await api.del(`${base}/${rel.relacionada_id}/${rel.tipo}/${rel.data_inicio}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Relação removida (dos dois lados).');
    void carregar();
  }

  async function salvarEdicao(rel: RelacaoPessoalCad, fim: string, obs: string) {
    const r = await api.patch(`${base}/${rel.relacionada_id}/${rel.tipo}/${rel.data_inicio}`, {
      data_fim: fim || null,
      observacoes: obs.trim() || null,
    });
    if (!r.ok) return void toast.error(r.erro);
    setEditando(null);
    toast.success('Relação atualizada (dos dois lados).');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users2 className="size-4 text-accent" />
          Relações pessoais
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Laços com outras pessoas cadastradas — parentesco e vínculos sociais. Cada laço vira
          aresta no grafo e aparece dos dois lados.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, relacoes.length === 0, 'Nenhuma relação registrada ainda.') ?? (
          <div className="flex flex-col gap-2">
            {relacoes.map((rel) => {
              const chave = `${rel.relacionada_id}/${rel.tipo}/${rel.data_inicio}`;
              return editando === chave ? (
                <FormEdicaoRelacao
                  key={chave}
                  rel={rel}
                  onCancelar={() => setEditando(null)}
                  onSalvar={(fim, obs) => void salvarEdicao(rel, fim, obs)}
                />
              ) : (
                <div
                  key={chave}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-[15px] text-text">{rel.relacionada.nome}</span>
                    <span className="tipo-corpo-pequeno text-text-tertiary">
                      {rel.relacionada.cpf && (
                        <span className="tipo-dado">{formatar(rel.relacionada.cpf, 'cpf')} · </span>
                      )}
                      {periodoLegivel(rel.data_inicio, rel.data_fim)}
                      {rel.observacoes && ` · ${rel.observacoes}`}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Chip aceso>{rotuloRelacao(rel.tipo)}</Chip>
                    <Button variant="fantasma" size="sm" onClick={() => setEditando(chave)} aria-label="Editar">
                      <Pencil />
                    </Button>
                    <Button
                      variant="fantasma"
                      size="sm"
                      className="text-danger hover:text-danger"
                      onClick={() => void remover(rel)}
                      aria-label="Remover"
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DrawerAdicionar
          titulo="Nova relação pessoal"
          descricao="Busque a pessoa (ou cadastre-a na hora) e diga o laço — ele aparece dos dois lados."
          rotuloAbrir="Adicionar relação"
          rotuloSalvar="Vincular"
          podeSalvar={!!alvo && !!tipo}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limparForm}
        >
          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Pessoa</span>
            <ComboBuscaEntidade config={BUSCA_PESSOA} onSelecionar={setAlvo} excluirId={pessoaId} />
          </div>
          <label className="flex flex-col gap-2">
            <span className="tipo-rotulo">É o(a)…</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
            >
              <option value="">Escolha a relação</option>
              {RELACOES.map((g) => (
                <optgroup key={g.grupo} label={g.grupo}>
                  {g.itens.map((i) => (
                    <option key={i.valor} value={i.valor}>
                      {i.rotulo}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <CamposVinculo
            inicio={inicio}
            fim={fim}
            observacoes={observacoes}
            onInicio={setInicio}
            onFim={setFim}
            onObservacoes={setObservacoes}
          />
          <p className="tipo-corpo-pequeno text-text-tertiary">
            A pessoa escolhida é <strong className="text-text">{tipo ? rotuloRelacao(tipo) : '…'}</strong> desta.
          </p>
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}

/** Edição in-place de uma relação: só fim e observação (tipo/início são a identidade do laço). */
function FormEdicaoRelacao({
  rel,
  onSalvar,
  onCancelar,
}: {
  rel: RelacaoPessoalCad;
  onSalvar: (fim: string, obs: string) => void;
  onCancelar: () => void;
}) {
  const [fim, setFim] = useState(rel.data_fim ?? '');
  const [obs, setObs] = useState(rel.observacoes ?? '');
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-strong bg-surface-2 px-3 py-3">
      <span className="tipo-rotulo text-text-secondary">
        {rel.relacionada.nome} — {rotuloRelacao(rel.tipo)}
      </span>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Até</span>
          <input
            type="date"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
          />
        </label>
        <label className="flex flex-1 flex-col gap-2">
          <span className="tipo-rotulo">Observações</span>
          <Input value={obs} onChange={(e) => setObs(e.target.value)} placeholder="…" />
        </label>
        <Button type="button" variant="secundario" onClick={() => onSalvar(fim, obs)}>
          Salvar
        </Button>
        <Button type="button" variant="fantasma" onClick={onCancelar} aria-label="Cancelar">
          <X />
        </Button>
      </div>
    </div>
  );
}
